import { useEffect, useRef, useState } from "react";
import { Moon } from "lucide-react";
import { useData } from "@/context/DataContext";
import { money, curSymbol } from "@/lib/money";
import { t } from "@/lib/i18n";
import Expander from "@/components/mm/Expander";

const NUM = [["zc", "Cash in hand & bank"], ["zg", "Gold you own (grams)"], ["zs", "Silver you own (grams)"], ["zi", "Shares / investments, market value"], ["zt", "Business stock & trade goods"], ["zr", "Money owed to you & expected back"], ["zo", "Other savings"]];
const DEF = { zb: "silver", zj: true };

const Row = ({ a, b, strong }) => <div className={`flex justify-between py-1.5 text-sm ${strong ? "font-bold" : ""}`}><span>{a}</span><b className="num">{b}</b></div>;

export default function Zakat({ contentOnly = false }) {
  const { db, saveSettings } = useData();
  const [z, setZ] = useState({ ...DEF, ...db.settings.zakat });
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (JSON.stringify(z) !== JSON.stringify({ ...DEF, ...db.settings.zakat })) saveSettings({ zakat: z }).catch(() => {}); }, 900);
    return () => clearTimeout(timer.current);
  }, [z]); // eslint-disable-line react-hooks/exhaustive-deps

  const v = (k) => Math.max(0, parseFloat(z[k]) || 0);
  const set = (k, val) => setZ({ ...z, [k]: val });
  const silver = z.zb === "silver", inc = !!z.zj, gp = v("zgp"), sp = v("zsp");
  const assets = v("zc") + (inc ? v("zg") * gp + v("zs") * sp : 0) + v("zi") + v("zt") + v("zr") + v("zo");
  const debt = Math.min(v("zd"), assets), net = Math.max(0, assets - debt);
  const nis = silver ? 612.36 * sp : 87.48 * gp;
  const owed = db.loans.filter((l) => !l.ret).reduce((s, l) => s + (l.amt || 0), 0);
  const c = curSymbol();
  const inp = (k, label) => (
    <label key={k} className="block"><span className="mb-1 block text-xs text-[var(--mu)]">{label}</span>
      <input className="mm-input num !py-2" type="number" min="0" step="any" value={z[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`zakat-${k}-input`} /></label>
  );
  const content = (
    <>
      <div className="mb-3 grid grid-cols-3 gap-2">
        <label className="block"><span className="mb-1 block text-xs text-[var(--mu)]">Nisab based on</span>
          <select className="mm-input !py-2" value={z.zb} onChange={(e) => set("zb", e.target.value)} data-testid="zakat-nisab-select"><option value="silver">Silver (612.36 g)</option><option value="gold">Gold (87.48 g)</option></select></label>
        {inp("zgp", `Gold price/g (${c})`)}
        {inp("zsp", `Silver price/g (${c})`)}
      </div>
      <details className="group" open>
        <summary className="cursor-pointer text-sm font-semibold">Your assets & debts</summary>
        <div className="mt-3 grid grid-cols-2 gap-2">{NUM.map(([k, l]) => inp(k, l))}{inp("zd", "Debts you must pay now")}</div>
        <label className="mt-3 flex items-center gap-2 text-xs text-[var(--mu)]"><input type="checkbox" checked={inc} onChange={(e) => set("zj", e.target.checked)} data-testid="zakat-include-gold-checkbox" /> Count my gold & silver (untick for worn jewellery under the Shafi'i, Maliki, Hanbali views)</label>
        <div className="mt-3 flex gap-2">
          <button className="mm-btn ghost sm" onClick={() => set("zd", String(owed))} data-testid="zakat-use-borrowed-button">Use my Borrowed items total</button>
          <button className="mm-btn ghost sm" onClick={() => setZ({ ...DEF })} data-testid="zakat-reset-button">Reset</button>
        </div>
      </details>
      <div className="mt-4 divide-y divide-[var(--bd)] border-t border-[var(--bd)] pt-2" data-testid="zakat-result">
        <Row a="Total zakatable assets" b={money(assets)} />
        <Row a="Less debts due now" b={`− ${money(debt)}`} />
        <Row a="Net wealth" b={money(net)} strong />
        <Row a={`Nisab (${silver ? "silver, 612.36 g" : "gold, 87.48 g"})`} b={nis ? money(nis) : "enter price per gram"} />
      </div>
      {!nis ? <p className="mt-3 text-sm text-[var(--dg)]">Enter the {silver ? "silver" : "gold"} price per gram to find the nisab.</p> :
        net >= nis ? (
          <div className="mt-3 rounded-[var(--ri)] p-4" style={{ background: "color-mix(in srgb, var(--ok) 14%, var(--bg))" }}>
            <div className="text-sm text-[var(--mu)]">Zakat is due (2.5%)</div><div className="num text-3xl font-bold" data-testid="zakat-due-amount">{money(net * 0.025)}</div>
          </div>
        ) : <div className="mt-3 rounded-[var(--ri)] bg-[var(--bg)] p-4 text-sm"><b>Below the nisab.</b> <span className="text-[var(--mu)]">It needs {money(nis - net)} more to reach the nisab.</span></div>}
      <p className="mt-3 text-[11px] text-[var(--mu)]">Zakat is 2.5% of net wealth held above the nisab for one full lunar year (hawl). Update prices often. For special cases, ask a scholar.</p>
    </>
  );
  return contentOnly ? content : <Expander tile icon={Moon} title={t("Zakat calculator")} testId="zakat-calculator-widget" badge={nis > 0 && net >= nis && <span className="num text-sm font-bold text-[var(--ok)]" data-testid="zakat-badge">{money(net * 0.025)}</span>}>{content}</Expander>;
}
